// src/components/tour-packages/TourPackageTable.tsx
import React, { useEffect, useState, type ReactNode } from "react";
import {
  FiEdit2,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
} from "react-icons/fi";

import Input from "@/components/shared/ui/Input";
import Select from "@/components/shared/ui/Select";
import type {
  TourRow,
  TourFilters,
} from "@/pages/tour-packages/hooks/useTourPackagesPage";

/* ============================================================================
   Small table helpers
============================================================================ */

function Th({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <th
      className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return <td className={`px-5 py-3.5 ${className}`}>{children}</td>;
}

/* Compact column filter popover */
function ColumnFilter({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest?.("[data-popover]")) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  return (
    <div className="relative" data-popover>
      <button
        type="button"
        className="inline-flex items-center justify-center rounded p-0.5 text-gray-500 hover:text-gray-700"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Filter ${label}`}
        title={`Filter ${label}`}
      >
        <FiFilter />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-64 rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
          <div className="mb-2 text-xs font-semibold text-gray-700">
            {label} Filter
          </div>
          <div className="space-y-2">{children}</div>
          <div className="mt-3 text-right">
            <button
              className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* Status pill */
function StatusPill({ status }: { status: string }) {
  const base =
    "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium";
  if (status === "ACTIVE") {
    return (
      <span
        className={`${base} bg-emerald-50 text-emerald-700 border border-emerald-100`}
      >
        Active
      </span>
    );
  }
  if (status === "DRAFT") {
    return (
      <span
        className={`${base} bg-amber-50 text-amber-700 border border-amber-100`}
      >
        Draft
      </span>
    );
  }
  return (
    <span
      className={`${base} bg-gray-50 text-gray-600 border border-gray-200`}
    >
      {status}
    </span>
  );
}

/* ============================================================================
   Component
============================================================================ */

export default function TourPackageTable({
  rows,
  serialStart,
  filters,
  setFilters,
  page,
  setPage,
  pageSize,
  totalItems,
  totalPages,
  onEdit,
  onDelete,
}: {
  rows: TourRow[];
  serialStart: number;
  filters: TourFilters;
  setFilters: React.Dispatch<React.SetStateAction<TourFilters>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  onEdit: (row: TourRow) => void;
  onDelete: (row: TourRow) => void;
}) {
  const start = serialStart;

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr className="border-b border-gray-200">
              {/* S.No – sticky left (z stays below the app header's z-20) */}
              <Th className="w-16 min-w-[64px] sticky left-0 z-10 bg-gray-50">
                S.No
              </Th>

              {/* Tour – sticky next to S.No, wider */}
              <Th className="min-w-[320px] sticky left-16 z-10 bg-gray-50">
                <div className="flex items-center gap-1.5">
                  <span>Tour</span>
                  <ColumnFilter label="Tour name">
                    <Input
                      placeholder="Contains…"
                      value={filters.name || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, name: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              {/* Type */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Type</span>
                  <ColumnFilter label="Tour type">
                    <Input
                      placeholder="Contains…"
                      value={filters.tourType || ""}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          tourType: e.target.value,
                        }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              {/* Supplier */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Supplier</span>
                  <ColumnFilter label="Supplier">
                    <Input
                      placeholder="Contains…"
                      value={filters.supplier || ""}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          supplier: e.target.value,
                        }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              {/* Nights / Days */}
              <Th>
                <span>Nights / Days</span>
              </Th>

              {/* Status */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Status</span>
                  <ColumnFilter label="Status">
                    <Select
                      value={filters.status || "ALL"}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          status: e.target.value as any,
                        }))
                      }
                      options={["ALL", "ACTIVE", "DRAFT", "INACTIVE"]}
                    />
                  </ColumnFilter>
                </div>
              </Th>

              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-5 py-10 text-center text-sm text-gray-500"
                >
                  No tours found.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr
                  key={r.id}
                  className="cursor-pointer hover:bg-gray-50/60"
                  onClick={() => onEdit(r)}
                >
                  {/* S.No – sticky */}
                  <Td className="w-16 min-w-[64px] sticky left-0 z-10 bg-white text-gray-700">
                    {start + i + 1}
                  </Td>

                  {/* Tour – sticky, widened */}
                  <Td className="text-gray-900 min-w-[320px] sticky left-16 z-10 bg-white">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{r.name}</span>
                      <div className="flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
                        {r.tourCode && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5">
                            Code: {r.tourCode}
                          </span>
                        )}
                        <span className="rounded-full bg-sky-50 px-2 py-0.5 text-sky-700">
                          {r.nights}N / {r.days}D
                        </span>
                        {r.isMultiCity && (
                          <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-700">
                            Multi-city
                          </span>
                        )}
                      </div>
                    </div>
                  </Td>

                  {/* Type */}
                  <Td className="text-gray-700">{r.tourTypeName || "—"}</Td>

                  {/* Supplier */}
                  <Td className="text-gray-700">{r.supplierName || "—"}</Td>

                  {/* Nights / Days (compact repeat) */}
                  <Td className="text-gray-700">
                    {r.nights} / {r.days}
                  </Td>

                  {/* Status */}
                  <Td className="text-gray-700">
                    <StatusPill status={r.status} />
                  </Td>

                  {/* Actions */}
                  <Td>
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(r);
                        }}
                        aria-label="Edit"
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(r);
                        }}
                        aria-label="Delete"
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile list */}
      <div className="md:hidden divide-y divide-gray-200">
        {rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            No tours found.
          </div>
        ) : (
          rows.map((r, i) => (
            <div
              key={r.id}
              className="cursor-pointer p-4"
              onClick={() => onEdit(r)}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {start + i + 1}. {r.name}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {r.tourTypeName || "—"} • {r.nights}N/{r.days}D
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(r);
                    }}
                    aria-label="Edit"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(r);
                    }}
                    aria-label="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-700">
                <div>
                  <span className="text-gray-500">Supplier:</span>{" "}
                  {r.supplierName || "—"}
                </div>
                <div>
                  <span className="text-gray-500">Nights / Days:</span>{" "}
                  {r.nights} / {r.days}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-3">
        <div className="text-xs text-gray-500">
          Showing{" "}
          <span className="font-medium text-gray-700">
            {totalItems === 0 ? 0 : start + 1}
          </span>{" "}
          to{" "}
          <span className="font-medium text-gray-700">
            {Math.min(start + pageSize, totalItems)}
          </span>{" "}
          of{" "}
          <span className="font-medium text-gray-700">{totalItems}</span>{" "}
          results
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <FiChevronLeft /> Prev
          </button>

          {/* Numbered buttons */}
          <div className="hidden items-center gap-1 sm:flex">
            {Array.from({ length: Math.min(totalPages, 6) }).map((_, idx) => {
              const n = idx + 1;
              const active = n === page;
              return (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`rounded-md px-2.5 py-1.5 text-sm ${
                    active
                      ? "bg-sky-600 text-white"
                      : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {n}
                </button>
              );
            })}
            {totalPages > 6 && (
              <span className="px-1 text-sm text-gray-500">…</span>
            )}
          </div>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Next <FiChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}
